import { getCollection, convertTimestamp } from './firestoreService';
import { subDays } from 'date-fns';

export interface EmailMetrics {
  emailType: string;
  sent: number;
  delivered: number;
  opened: number;
  clicked: number;
  bounced: number;
}

export interface CampaignEffectiveness {
  campaignType: string;
  conversionRate: number;
  subscriptionRate: number;
  recoveryRate: number;
}

export interface EmailAnalytics {
  emailMetrics: EmailMetrics[];
  campaignEffectiveness: CampaignEffectiveness[];
  totalSent: number;
  totalDelivered: number;
  deliveryRate: number;
  openRate: number;
  clickRate: number;
  bounceRate: number;
}

interface EmailEventDoc {
  eventType: string;
  emailType: string;
  toEmail?: string;
  success: boolean;
  error?: string;
  timestamp: any;
  platform?: string;
}

export const getEmailAnalytics = async (
  days: number = 30
): Promise<EmailAnalytics> => {
  try {
    const startDate = subDays(new Date(), days);

    const emailEvents = await getCollection<EmailEventDoc>(
      'analytics_email_events',
      [
        {
          field: 'timestamp',
          operator: '>=',
          value: startDate.toISOString(),
        },
      ],
      'timestamp',
      'desc',
      10000
    );

    const emailTypeMap = new Map<string, {
      sent: number;
      delivered: number;
      opened: number;
      clicked: number;
      bounced: number;
    }>();

    emailEvents.forEach((event) => {
      const emailType = event.emailType || 'unknown';
      const success = event.success !== false;
      const bounced = !success && !!event.error;

      if (!emailTypeMap.has(emailType)) {
        emailTypeMap.set(emailType, { sent: 0, delivered: 0, opened: 0, clicked: 0, bounced: 0 });
      }

      const metrics = emailTypeMap.get(emailType)!;
      metrics.sent++;
      if (success) metrics.delivered++;
      if (bounced) metrics.bounced++;
    });

    const emailMetrics: EmailMetrics[] = Array.from(emailTypeMap.entries())
      .map(([emailType, metrics]) => ({
        emailType,
        ...metrics,
      }))
      .sort((a, b) => b.sent - a.sent);

    const campaignEffectiveness: CampaignEffectiveness[] = emailMetrics.map((metric) => ({
      campaignType: metric.emailType,
      conversionRate: 0,
      subscriptionRate: 0,
      recoveryRate: 0,
    }));

    const totalSent = emailMetrics.reduce((sum, m) => sum + m.sent, 0);
    const totalDelivered = emailMetrics.reduce((sum, m) => sum + m.delivered, 0);
    const totalOpened = emailMetrics.reduce((sum, m) => sum + m.opened, 0);
    const totalClicked = emailMetrics.reduce((sum, m) => sum + m.clicked, 0);
    const totalBounced = emailMetrics.reduce((sum, m) => sum + m.bounced, 0);

    return {
      emailMetrics,
      campaignEffectiveness,
      totalSent,
      totalDelivered,
      deliveryRate: totalSent > 0 ? (totalDelivered / totalSent) * 100 : 0,
      openRate: totalDelivered > 0 ? (totalOpened / totalDelivered) * 100 : 0,
      clickRate: totalDelivered > 0 ? (totalClicked / totalDelivered) * 100 : 0,
      bounceRate: totalSent > 0 ? (totalBounced / totalSent) * 100 : 0,
    };
  } catch (error) {
    console.error('Error calculating email analytics:', error);
    throw error;
  }
};
